import type { StoreV2 } from 'api/models/unified';
import { SearchBox } from 'components';
import { CheckboxInput, FakeLabel, SelectInput } from 'components/forms/hook-form';
import SelectItemAdditionalInfo from 'components/forms/SelectItemAdditionalInfo';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useUsers } from 'helpers/usersHelper';
import { removeDiacritics, useValidators } from 'helpers/validation';
import { StorePermissionArray } from 'pages/material/models';
import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { StoreFormValues } from './StoreFormV2';

interface StoreFormAvailableForHandlerProps {
  store?: StoreV2;
}

const StoreFormAvailableForHandler = ({ store }: StoreFormAvailableForHandlerProps) => {
  const { tPermission, tMaterial } = useCustomTranslation();
  const { watch, setValue } = useFormContext<StoreFormValues>();

  const [searchPhrase, setSearchPhrase] = useState('');

  const { filterDisabledTechniciansForForm } = useUsers();
  const validators = useValidators();

  const originalIds = useMemo(
    () => store?.availableFor.map((permission) => permission.userId) || [],
    [store?.availableFor]
  );

  const value = watch('availableFor');

  const technicianIds = value ? Object.keys(value) : [];

  return (
    <div className="StoreFormAvailableForHandler">
      <div className="StoreFormAvailableForHandler_header">
        <FakeLabel fieldName="availableFor" label={tMaterial('store_available_for')} />
        <SearchBox onValueChanged={setSearchPhrase} />
      </div>
      <div className="StoreFormAvailableForHandler_content">
        {filterDisabledTechniciansForForm(originalIds, technicianIds)
          .filter((x) => removeDiacritics(x.name).toLocaleLowerCase().includes(searchPhrase))
          .map((user) => {
            const isUserEnabled = value ? value[user.id]?.enabled : false;

            return (
              <div key={user.id} className="StoreFormAvailableForHandler_item">
                <CheckboxInput name={`availableFor.${user.id}.enabled`} />
                <div
                  className="StoreFormAvailableForHandler_item-info"
                  onClick={() =>
                    setValue(`availableFor.${user.id}.enabled`, !isUserEnabled, {
                      shouldDirty: true
                    })
                  }
                >
                  <span className="StoreFormAvailableForHandler_item-user">{user.name}</span>
                  {user.additionalInfo && (
                    <SelectItemAdditionalInfo additionalInfo={user.additionalInfo} />
                  )}
                </div>
                <SelectInput
                  items={StorePermissionArray.map((permission) => ({
                    id: permission,
                    name: tPermission(permission)
                  }))}
                  name={`availableFor.${user.id}.permission`}
                  disabled={user.disabled || !isUserEnabled}
                  required={isUserEnabled}
                  options={{
                    required: isUserEnabled ? validators.required : undefined
                  }}
                  placeholder={tPermission('select_placeholder')}
                  size="small"
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default StoreFormAvailableForHandler;
